import React, { useState } from "react";
import {
  useGetProcessorSpecsQuery,
  useCreateProcessorSpecMutation,
  useGetNamespacesQuery,
  CustomerFieldId,
} from "../../api/customerFieldApi";
import { ProcessorSpecItem } from "./ProcessorSpecItem";
import { Button } from "../../components/tailwind/button/Button";
import { Dropdown } from "../../components/tailwind/dropdown/Dropdown";

interface Props {
  customerFieldId: CustomerFieldId;
}

export const ProcessorSpecExpander: React.FC<Props> = ({ customerFieldId }) => {
  const { data, isLoading, error } = useGetProcessorSpecsQuery(customerFieldId);
  const { data: namespacesData } = useGetNamespacesQuery();
  const [
    createSpec,
    { isLoading: isCreating },
  ] = useCreateProcessorSpecMutation();

  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newNamespace, setNewNamespace] = useState("");

  const handleCreateSpec = async (): Promise<void> => {
    try {
      await createSpec({
        customer_field_id: customerFieldId,
        namespace_name: newNamespace || undefined,
      }).unwrap();
      setIsAddingNew(false);
      setNewNamespace("");
    } catch (err) {
      console.error("Failed to create processor spec:", err);
    }
  };

  if (isLoading) {
    return (
      <div className="tw-px-4 tw-py-6 tw-bg-gray-50 tw-flex tw-items-center tw-justify-center">
        <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-b-2 tw-border-gray-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-px-4 tw-py-4 tw-bg-red-50 tw-text-red-700 tw-text-sm">
        Failed to load processors. Please try again.
      </div>
    );
  }

  const specs = data?.processor_specs || [];
  const processorChoices = data?.processor_choices || [];
  const namespaces = namespacesData?.namespaces || ["Default"];

  // Group specs by namespace
  const specsByNamespace = specs.reduce((acc, spec) => {
    const ns = spec.namespace_name || "Default";
    if (!acc[ns]) acc[ns] = [];
    acc[ns].push(spec);
    return acc;
  }, {} as Record<string, typeof specs>);

  const namespaceOptions = namespaces.map((ns) => ({
    key: ns,
    value: ns,
    text: ns,
  }));

  return (
    <div className="tw-bg-gray-50 tw-border-t tw-border-gray-200">
      {/* Add New Processor */}
      <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-200">
        {isAddingNew ? (
          <div className="tw-flex tw-items-end tw-gap-3">
            <div className="tw-w-48">
              <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">
                Namespace
              </label>
              <Dropdown
                options={namespaceOptions}
                value={newNamespace}
                onChange={(value) => setNewNamespace(value as string)}
                placeholder="Default"
                clearable
              />
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={handleCreateSpec}
              disabled={isCreating}
            >
              {isCreating ? "Adding..." : "Add Processor"}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setIsAddingNew(false);
                setNewNamespace("");
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setIsAddingNew(true)}
          >
            <i className="material-icons tw-text-sm tw-mr-1">add</i>
            Add Processor
          </Button>
        )}
      </div>

      {/* Processor Specs List */}
      {specs.length === 0 ? (
        <div className="tw-px-4 tw-py-6 tw-text-center tw-text-gray-500 tw-text-sm">
          No processors configured for this field.
        </div>
      ) : (
        <div className="tw-divide-y tw-divide-gray-200">
          {Object.entries(specsByNamespace).map(([namespace, nsSpecs]) => (
            <div key={namespace}>
              {/* Namespace Header */}
              {Object.keys(specsByNamespace).length > 1 && (
                <div className="tw-px-4 tw-py-2 tw-bg-gray-100 tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">
                  {namespace}
                </div>
              )}
              {/* Specs in this namespace */}
              {nsSpecs.map((spec) => (
                <ProcessorSpecItem
                  key={spec.id}
                  spec={spec}
                  processorChoices={processorChoices}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
